import type { TOC } from '@ember/component/template-only';

interface StepCardSignature {
  Args: {
    /** Step number to display (1, 2, 3, etc.) */
    step: number;
    /** Main title for the step */
    title: string;
    /** Description text explaining the step */
    description: string;
    /** Whether the step is completed (default: false) */
    completed?: boolean;
    /** Whether the step is the current/active step (default: false) */
    active?: boolean;
  };
  Blocks: {
    /** Action button or content to display on the right side */
    action?: [];
  };
  Element: HTMLDivElement;
}

/**
 * StepCard - Onboarding/progress step card with number indicator
 *
 * Displays a numbered step with title, description, and optional action.
 * Supports completed and active states for visual feedback.
 *
 * @example Basic usage
 * ```gts
 * import StepCard from 'tio-ui/components/step-card';
 *
 * <StepCard
 *   @step={{1}}
 *   @title="Upload your loan documents"
 *   @description="Upload loan statements or NSLDS file to see your balances"
 * >
 *   <:action>
 *     <Button @intent="primary" @appearance="outlined">Upload</Button>
 *   </:action>
 * </StepCard>
 * ```
 *
 * @example Completed step
 * ```gts
 * <StepCard @step={{1}} @title="Profile completed" @description="Your career profile is set up" @completed={{true}} />
 * ```
 */
const StepCard: TOC<StepCardSignature> = <template>
  <div
    data-test-step-card
    class="tio-step-card tio-row-loose p-4
      {{if @completed 'tio-step-card--completed' (if @active 'tio-step-card--active')}}"
    ...attributes
  >
    <div
      data-test-step-card-indicator
      class="tio-row-center tio-step-card__indicator w-8 h-8 shrink-0"
    >
      {{#if @completed}}
        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="3"
            d="M5 13l4 4L19 7"
          />
        </svg>
      {{else}}
        {{@step}}
      {{/if}}
    </div>
    <div class="tio-fill-min">
      <div data-test-step-card-title class="tio-step-card__title app-rich-text">
        {{@title}}
      </div>
      <div data-test-step-card-description class="tio-step-card__desc app-rich-text">
        {{@description}}
      </div>
    </div>
    {{#if (has-block "action")}}
      <div data-test-step-card-action class="shrink-0">
        {{yield to="action"}}
      </div>
    {{/if}}
  </div>
</template>;

export default StepCard;

export type { StepCardSignature };
